import { BrowserRouter as Router, Navigate, Route, Routes, useParams } from "react-router-dom";
import AuthPageContainer from "./components/Authentication/AuthPageContainer";
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import EmailChangeVerificationPage from "./pages/EmailChangeVerificationPage";
import AvailabilityPage from "./pages/AvailabilityPage";
import ServicePage from "./pages/ServicePage";
import ClientListPage from "./pages/ClientListPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import CalendarPage from "./pages/CalendarPage";
import SettingsPage from "./pages/SettingsPage";
import BookingLinkConfigPage from "./pages/BookingLinkConfigPage";
import DashboardPage from "./pages/DashboardPage";
import PublicBookingPage from "./pages/PublicBookingPage";
import AppointmentManagementPage from "./pages/AppointmentManagementPage";
import NotFoundPage from "./pages/NotFoundPage";
import Layout from "./components/Layout/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import useDocumentTitle from "./hooks/useDocumentTitle";
import { isBookingRootRouteEnabled } from "./utility/environment";

// Slugs reserved at the routing layer so `book.*` hosts don't treat admin
// URLs as public booking pages. The bare `/:slug` route below outranks the
// protected admin wrapper (`/*`) in React Router v6's specificity ranking,
// so without this guard, visiting e.g. book.anyspot.io/dashboard would
// trigger GET /api/public/booking/dashboard and render NotFoundPage only
// after a real backend 404.
//
// IMPORTANT: keep this in sync with the admin and auth routes registered
// in <AppContent /> below. Adding a new top-level admin route without
// adding it here will silently regress on `book.*` hosts.
const RESERVED_SLUGS = new Set([
  // Protected admin
  "dashboard",
  "availability",
  "services",
  "clients",
  "calendar",
  "settings",
  "booking-page",
  // Auth
  "signin",
  "signup",
  "forgot-password",
  "reset-password",
  "verify-email-change",
  // Other top-level routes
  "book",
  "manage-booking",
  "404",
]);

const PublicBookingSlugRoute = () => {
  const { slug } = useParams();
  if (RESERVED_SLUGS.has(slug)) {
    return <NotFoundPage />;
  }
  return <PublicBookingPage />;
};

// Wrapper component that sits inside Router context to access useLocation
const AppContent = () => {
  // Set dynamic page titles based on current route
  useDocumentTitle();
  const bookingRootRoute = isBookingRootRouteEnabled();

  return (
    <Routes>
      {/* Public Booking Page Routes (no authentication) */}
      <Route path="/book/:slug" element={<PublicBookingPage />} />
      <Route path="/manage-booking/:slug" element={<AppointmentManagementPage />} />
      <Route path="/404" element={<NotFoundPage />} />

      {/* Authentication Page Routes (no AuthProvider) */}
      <Route element={<AuthPageContainer />}>
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email-change" element={<EmailChangeVerificationPage />} />
      </Route>

      {/* Protected Application Routes (with AuthProvider) */}
      <Route
        path="/*"
        element={
          <AuthProvider>
            <Routes>
              <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/availability" element={<AvailabilityPage />} />
                <Route path="/services" element={<ServicePage />} />
                <Route path="/clients" element={<ClientListPage />} />
                <Route path="/clients/:id" element={<ClientDetailPage />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/:tab" element={<SettingsPage />} />
                <Route path="/booking-page" element={<BookingLinkConfigPage />} />
                <Route
                  path="/booking-page/:tab"
                  element={<BookingLinkConfigPage />}
                />
              </Route>
            </Routes>
          </AuthProvider>
        }
      />

      {/* book.* hosts (e.g. book.anyspot.io): serve PublicBookingPage at
          /:slug (root path). Admin/auth route names are filtered via
          RESERVED_SLUGS so visiting book.anyspot.io/dashboard renders
          NotFoundPage synchronously instead of firing a 404'd backend call.
          Auto-enabled at runtime by isBookingRootRouteEnabled() when
          hostname starts with `book.`. /book/:slug above still works on
          every host for back-compat. */}
      {bookingRootRoute && (
        <Route path="/:slug" element={<PublicBookingSlugRoute />} />
      )}
    </Routes>
  );
};

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
export { RESERVED_SLUGS, PublicBookingSlugRoute };
